import React, { useState, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Shield, Moon, Zap, FlaskConical, Watch, Brain, Wind, Share2, Save, ChevronRight, TrendingUp, Star, ExternalLink } from 'lucide-react';
import { useResults } from '../lib/useResults';
import { EmailResultsGate } from '../components/tools/EmailResultsGate';

const CATEGORIES = [
  { id: 'sleep', label: 'Sleep', icon: Moon, color: '#818cf8' },
  { id: 'recovery', label: 'Recovery', icon: Zap, color: '#E63946' },
  { id: 'nutrition', label: 'Nutrition & Supplements', icon: FlaskConical, color: '#22c55e' },
  { id: 'tracking', label: 'Tracking', icon: Watch, color: '#00F0FF' },
  { id: 'mental', label: 'Mental Performance', icon: Brain, color: '#a78bfa' },
  { id: 'environment', label: 'Environment', icon: Wind, color: '#f59e0b' },
];

const QUESTIONS = [
  // SLEEP (0-3)
  { id: 'sleep_hours', cat: 'sleep', q: 'How many hours of sleep do you average per night?', options: [
    { label: 'Less than 6 hours', score: 1 }, { label: '6-7 hours', score: 3 }, { label: '7-8 hours', score: 7 }, { label: '8-9 hours (optimized)', score: 10 },
  ]},
  { id: 'sleep_quality', cat: 'sleep', q: 'How would you rate your sleep quality?', options: [
    { label: 'Poor — wake up tired', score: 1 }, { label: 'Fair — inconsistent', score: 4 }, { label: 'Good — mostly rested', score: 7 }, { label: 'Excellent — deep & restorative', score: 10 },
  ]},
  { id: 'sleep_tracking', cat: 'sleep', q: 'Do you track your sleep with a device?', options: [
    { label: 'No tracking', score: 0 }, { label: 'Phone app only', score: 3 }, { label: 'Wearable (Fitbit, Apple Watch)', score: 6 }, { label: 'Dedicated tracker (Oura, Whoop, Elemind)', score: 10 },
  ]},
  // RECOVERY (4-6)
  { id: 'recovery_methods', cat: 'recovery', q: 'Which recovery methods do you use regularly?', multi: true, options: [
    { label: 'None', score: 0 }, { label: 'Stretching / Foam rolling', score: 2 }, { label: 'Massage gun (Theragun)', score: 3 }, { label: 'Cold plunge / Ice bath', score: 4 },
    { label: 'Infrared sauna', score: 4 }, { label: 'Red light therapy', score: 4 }, { label: 'O₂ Nanobubble therapy (Bimini)', score: 6 },
  ]},
  { id: 'recovery_freq', cat: 'recovery', q: 'How often do you actively recover (beyond rest days)?', options: [
    { label: 'Rarely / never', score: 1 }, { label: '1-2x per week', score: 4 }, { label: '3-4x per week', score: 7 }, { label: 'Daily protocol', score: 10 },
  ]},
  { id: 'recovery_peptides', cat: 'recovery', q: 'Do you use peptides for recovery?', options: [
    { label: 'No', score: 0 }, { label: 'Interested but haven\'t started', score: 2 }, { label: 'Yes — 1 peptide', score: 6 }, { label: 'Yes — stacking 2+ peptides', score: 10 },
  ]},
  // NUTRITION (7-9)
  { id: 'diet_tracking', cat: 'nutrition', q: 'Do you track your nutrition / macros?', options: [
    { label: 'No tracking', score: 1 }, { label: 'Loosely — general awareness', score: 4 }, { label: 'Yes — app-based tracking', score: 7 }, { label: 'Precise — with CGM or metabolic testing', score: 10 },
  ]},
  { id: 'supplements', cat: 'nutrition', q: 'What\'s your supplement stack like?', options: [
    { label: 'No supplements', score: 0 }, { label: 'Basic multivitamin', score: 3 }, { label: 'Targeted stack (D3, Mg, Omega-3, Creatine)', score: 7 }, { label: 'Personalized (blood-test informed) + nootropics', score: 10 },
  ]},
  { id: 'fasting', cat: 'nutrition', q: 'Do you practice intermittent fasting or time-restricted eating?', options: [
    { label: 'No', score: 0 }, { label: 'Sometimes / casually', score: 3 }, { label: 'Yes — consistent schedule (16:8, etc.)', score: 7 }, { label: 'Yes — with metabolic tracking (Lumen/CGM)', score: 10 },
  ]},
  // TRACKING (10-11)
  { id: 'wearable', cat: 'tracking', q: 'What wearable tech do you use?', multi: true, options: [
    { label: 'None', score: 0 }, { label: 'Basic fitness tracker (steps)', score: 2 }, { label: 'Smartwatch (Apple Watch, Garmin)', score: 4 }, { label: 'Oura Ring', score: 5 },
    { label: 'WHOOP Strap', score: 5 }, { label: 'CGM (Continuous Glucose Monitor)', score: 5 },
  ]},
  { id: 'bloodwork', cat: 'tracking', q: 'How often do you get bloodwork / biomarker testing?', options: [
    { label: 'Never / only when sick', score: 0 }, { label: 'Annually (basic panel)', score: 3 }, { label: '2-4x per year (comprehensive)', score: 7 }, { label: 'Quarterly + DNA testing', score: 10 },
  ]},
  // MENTAL (12-13)
  { id: 'meditation', cat: 'mental', q: 'Do you practice meditation or breathwork?', options: [
    { label: 'No', score: 0 }, { label: 'Occasionally', score: 3 }, { label: 'Regular practice (3-5x/week)', score: 7 }, { label: 'Daily + use EEG/neurofeedback device', score: 10 },
  ]},
  { id: 'stress', cat: 'mental', q: 'How do you manage stress?', options: [
    { label: 'I don\'t — just push through', score: 1 }, { label: 'Exercise helps', score: 4 }, { label: 'Active practices (journaling, nature, sauna)', score: 7 }, { label: 'Comprehensive (HRV tracking, vagus nerve stimulation, breathwork)', score: 10 },
  ]},
  // ENVIRONMENT (14-15)
  { id: 'air_quality', cat: 'environment', q: 'Do you optimize your air or water quality?', options: [
    { label: 'No', score: 0 }, { label: 'Water filter only', score: 3 }, { label: 'HEPA air purifier + water filter', score: 7 }, { label: 'Full optimization (air, water, EMF awareness)', score: 10 },
  ]},
  { id: 'light', cat: 'environment', q: 'How do you manage light exposure?', options: [
    { label: 'Don\'t think about it', score: 0 }, { label: 'Night mode on phone', score: 2 }, { label: 'Blue light glasses in evening', score: 5 }, { label: 'Full protocol (morning sunlight, blue blockers, sunrise alarm, blackout room)', score: 10 },
  ]},
];

const TIERS = [
  { min: 0, max: 19, label: 'Novice', color: '#71717a', desc: 'You\'re just getting started. Small changes will make a huge impact.' },
  { min: 20, max: 39, label: 'Beginner', color: '#f59e0b', desc: 'You have some good habits. Time to level up your protocol.' },
  { min: 40, max: 59, label: 'Intermediate', color: '#00F0FF', desc: 'Solid foundation. You\'re ahead of 80% of people.' },
  { min: 60, max: 79, label: 'Advanced', color: '#22c55e', desc: 'Impressive protocol. You\'re optimizing multiple systems.' },
  { min: 80, max: 100, label: 'Elite', color: '#e8ff00', desc: 'Top-tier biohacker. You\'re operating at peak human performance.' },
];

const RECOMMENDATIONS = {
  sleep: [
    { threshold: 5, text: 'Start tracking sleep with an Oura Ring to identify patterns', link: '/biohacking', linkLabel: 'Sleep Tools' },
    { threshold: 5, text: 'Try a sunrise alarm clock for natural wake-up', link: '/biohacking', linkLabel: 'View Options' },
    { threshold: 8, text: 'Consider Elemind headband for brainwave-optimized sleep onset', link: '/biohacking', linkLabel: 'Advanced Sleep' },
  ],
  recovery: [
    { threshold: 4, text: 'Add a massage gun to your post-workout routine', link: '/biohacking', linkLabel: 'Recovery Tools' },
    { threshold: 5, text: 'Explore O₂ Nanobubble therapy — 46% faster recovery', link: '/hydrotherapy', linkLabel: 'HydroTherapy Hub' },
    { threshold: 7, text: 'Build a peptide stack for targeted recovery', link: '/tools/peptide-stack', linkLabel: 'Peptide Stack Builder' },
  ],
  nutrition: [
    { threshold: 4, text: 'Start tracking macros with our calculator', link: '/tools/macro-calculator', linkLabel: 'Macro Calculator' },
    { threshold: 5, text: 'Build a targeted supplement stack (D3, Mg, Omega-3, Creatine)', link: '/biohacking', linkLabel: 'Supplement Guide' },
    { threshold: 8, text: 'Add a CGM for precision nutrition data', link: '/biohacking', linkLabel: 'CGM Guide' },
  ],
  tracking: [
    { threshold: 4, text: 'Get a wearable — Oura Ring or WHOOP for comprehensive data', link: '/biohacking', linkLabel: 'Wearables' },
    { threshold: 6, text: 'Schedule quarterly bloodwork for biomarker trends', link: '/biohacking', linkLabel: 'Blood Testing' },
    { threshold: 8, text: 'Track body composition trends with our tool', link: '/tools/body-recomp', linkLabel: 'Body Recomp Tracker' },
  ],
  mental: [
    { threshold: 4, text: 'Start a 10-min daily meditation practice', link: '/biohacking', linkLabel: 'Mental Tools' },
    { threshold: 6, text: 'Try Apollo 2.0 for vagus nerve stimulation', link: '/biohacking', linkLabel: 'Apollo Guide' },
    { threshold: 8, text: 'Add neurofeedback with a Muse EEG headband', link: '/biohacking', linkLabel: 'EEG Devices' },
  ],
  environment: [
    { threshold: 3, text: 'Get blue light blocking glasses for evening screen use', link: '/biohacking', linkLabel: 'Blue Light' },
    { threshold: 5, text: 'Add a HEPA air purifier to your bedroom', link: '/biohacking', linkLabel: 'Air Purifiers' },
    { threshold: 8, text: 'Implement a full light management protocol', link: '/biohacking', linkLabel: 'Environment' },
  ],
};

const RadarChart = ({ scores, size = 240 }) => {
  const cx = size / 2, cy = size / 2, r = size / 2 - 30;
  const n = CATEGORIES.length;
  const angleStep = (2 * Math.PI) / n;
  const getPoint = (i, val) => {
    const angle = angleStep * i - Math.PI / 2;
    const pct = val / 10;
    return { x: cx + r * pct * Math.cos(angle), y: cy + r * pct * Math.sin(angle) };
  };
  const gridLevels = [0.25, 0.5, 0.75, 1];

  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="w-full max-w-[280px] mx-auto" data-testid="radar-chart">
      {/* Grid */}
      {gridLevels.map(lvl => {
        const pts = Array.from({ length: n }, (_, i) => {
          const angle = angleStep * i - Math.PI / 2;
          return `${cx + r * lvl * Math.cos(angle)},${cy + r * lvl * Math.sin(angle)}`;
        }).join(' ');
        return <polygon key={lvl} points={pts} fill="none" stroke="#27272a" strokeWidth="0.5" />;
      })}
      {/* Axes */}
      {CATEGORIES.map((c, i) => {
        const end = getPoint(i, 10);
        return <line key={c.id} x1={cx} y1={cy} x2={end.x} y2={end.y} stroke="#27272a" strokeWidth="0.5" />;
      })}
      {/* Data polygon */}
      <polygon
        points={CATEGORIES.map((c, i) => { const p = getPoint(i, scores[c.id] || 0); return `${p.x},${p.y}`; }).join(' ')}
        fill="rgba(230,57,70,0.15)" stroke="#E63946" strokeWidth="2"
      />
      {/* Data points + labels */}
      {CATEGORIES.map((c, i) => {
        const p = getPoint(i, scores[c.id] || 0);
        const lp = getPoint(i, 11.5);
        return (
          <g key={c.id}>
            <circle cx={p.x} cy={p.y} r="4" fill={c.color} />
            <text x={lp.x} y={lp.y} textAnchor="middle" dominantBaseline="central" fill={c.color} fontSize="8" fontWeight="bold">{c.label}</text>
          </g>
        );
      })}
    </svg>
  );
};

export default function BiohackingScorePage() {
  const [answers, setAnswers] = useState({});
  const [multiAnswers, setMultiAnswers] = useState({});
  const [currentQ, setCurrentQ] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const { saveResult } = useResults();

  const q = QUESTIONS[currentQ];
  const isMulti = q?.multi;
  const progress = ((currentQ + 1) / QUESTIONS.length) * 100;

  const handleSelect = (qId, score) => {
    setAnswers(prev => ({ ...prev, [qId]: score }));
    if (!isMulti && currentQ < QUESTIONS.length - 1) {
      setTimeout(() => setCurrentQ(prev => prev + 1), 200);
    } else if (!isMulti && currentQ === QUESTIONS.length - 1) {
      setTimeout(() => setShowResults(true), 200);
    }
  };

  const handleMultiToggle = (qId, optLabel, optScore) => {
    setMultiAnswers(prev => {
      const current = prev[qId] || [];
      if (optLabel === 'None') return { ...prev, [qId]: [] };
      const exists = current.find(c => c.label === optLabel);
      const updated = exists ? current.filter(c => c.label !== optLabel) : [...current.filter(c => c.label !== 'None'), { label: optLabel, score: optScore }];
      return { ...prev, [qId]: updated };
    });
  };

  const handleMultiNext = () => {
    const qId = q.id;
    const selections = multiAnswers[qId] || [];
    const totalScore = Math.min(selections.reduce((s, x) => s + x.score, 0), 10);
    setAnswers(prev => ({ ...prev, [qId]: totalScore }));
    if (currentQ < QUESTIONS.length - 1) setCurrentQ(prev => prev + 1);
    else setShowResults(true);
  };

  const catScores = useMemo(() => {
    const scores = {};
    CATEGORIES.forEach(c => {
      const qs = QUESTIONS.filter(q => q.cat === c.id);
      const total = qs.reduce((s, q) => s + (answers[q.id] || 0), 0);
      const max = qs.length * 10;
      scores[c.id] = max > 0 ? Math.round((total / max) * 10 * 10) / 10 : 0;
    });
    return scores;
  }, [answers]);

  const overallScore = useMemo(() => {
    const vals = Object.values(catScores);
    if (vals.length === 0) return 0;
    return Math.round((vals.reduce((s, v) => s + v, 0) / vals.length) * 10);
  }, [catScores]);

  const tier = TIERS.find(t => overallScore >= t.min && overallScore <= t.max) || TIERS[0];

  const weakest = useMemo(() => {
    return [...CATEGORIES].sort((a, b) => (catScores[a.id] || 0) - (catScores[b.id] || 0)).slice(0, 2);
  }, [catScores]);

  const recs = useMemo(() => {
    const result = [];
    CATEGORIES.forEach(c => {
      const score = catScores[c.id] || 0;
      const catRecs = RECOMMENDATIONS[c.id] || [];
      catRecs.forEach(r => { if (score < r.threshold) result.push({ ...r, category: c.label, catColor: c.color }); });
    });
    return result.slice(0, 6);
  }, [catScores]);

  const handleSave = useCallback(() => {
    saveResult('biohackingScore', { value: overallScore, label: tier.label, categories: catScores });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }, [overallScore, tier, catScores, saveResult]);

  const handleShare = useCallback(async () => {
    const text = `My Biohacking Score: ${overallScore}/100 (${tier.label})\n\n${CATEGORIES.map(c => `${c.label}: ${catScores[c.id]}/10`).join('\n')}\n\nTake the quiz: ninjathlete.com/tools/biohacking-score`;
    try {
      if (navigator.share) await navigator.share({ title: 'My Biohacking Score', text });
      else { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    } catch { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }
  }, [overallScore, tier, catScores]);

  const handleDownloadCard = useCallback(async () => {
    const canvas = document.createElement('canvas');
    canvas.width = 600;
    canvas.height = 800;
    const ctx = canvas.getContext('2d');

    // Background
    ctx.fillStyle = '#0a0a0a';
    ctx.fillRect(0, 0, 600, 800);

    // Top accent bar
    ctx.fillStyle = tier.color || '#E63946';
    ctx.fillRect(0, 0, 600, 4);

    // Brand
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 20px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('NINJATHLETE', 300, 50);

    // Title
    ctx.fillStyle = '#888888';
    ctx.font = '12px Arial';
    ctx.fillText('BIOHACKING SCORE', 300, 80);

    // Score circle
    ctx.beginPath();
    ctx.arc(300, 200, 80, 0, Math.PI * 2);
    ctx.strokeStyle = '#222';
    ctx.lineWidth = 6;
    ctx.stroke();

    // Score arc
    const pct = overallScore / 100;
    ctx.beginPath();
    ctx.arc(300, 200, 80, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * pct);
    ctx.strokeStyle = tier.color || '#E63946';
    ctx.lineWidth = 6;
    ctx.stroke();

    // Score number
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 48px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(String(overallScore), 300, 215);

    // Tier label
    ctx.fillStyle = tier.color || '#E63946';
    ctx.font = 'bold 16px Arial';
    ctx.fillText(tier.label.toUpperCase(), 300, 310);

    // Category bars
    const barY = 360;
    CATEGORIES.forEach((cat, i) => {
      const y = barY + i * 55;
      ctx.fillStyle = '#888';
      ctx.font = '12px Arial';
      ctx.textAlign = 'left';
      ctx.fillText(cat.label, 60, y);

      // Bar background
      ctx.fillStyle = '#1a1a1a';
      ctx.fillRect(60, y + 10, 400, 12);

      // Bar fill
      ctx.fillStyle = cat.color;
      ctx.fillRect(60, y + 10, (catScores[cat.id] / 10) * 400, 12);

      // Score
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 12px Arial';
      ctx.textAlign = 'right';
      ctx.fillText(`${catScores[cat.id]}/10`, 540, y);
    });

    // Footer
    ctx.fillStyle = '#444';
    ctx.font = '11px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('Take the quiz: ninjathlete.com/tools/biohacking-score', 300, 770);

    // Download
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = `biohacking-score-${overallScore}.png`;
    a.click();
  }, [overallScore, tier, catScores]);

  const handleRetake = () => {
    setAnswers({});
    setMultiAnswers({});
    setCurrentQ(0);
    setShowResults(false);
    setSaved(false);
  };

  // Quiz view
  if (!showResults) {
    return (
      <main className="pt-20 min-h-screen" data-testid="biohacking-score-page">
        <section className="container-ninja section-spacing">
          <div className="max-w-2xl mx-auto">
            <h1 className="sr-only">Biohacking Score Assessment — NinjAthlete</h1>
            <Link to="/tools" className="text-zinc-500 text-xs font-heading uppercase tracking-wider hover:text-white transition-colors inline-flex items-center gap-1 mb-6" data-testid="back-to-tools">
              <ArrowLeft className="w-3 h-3" /> All Tools
            </Link>

            {/* Progress bar */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-2">
                <span className="text-zinc-500 text-xs font-heading uppercase tracking-wider">Question {currentQ + 1} of {QUESTIONS.length}</span>
                <span className="text-zinc-600 text-xs">{Math.round(progress)}%</span>
              </div>
              <div className="h-1 bg-zinc-800 overflow-hidden">
                <div className="h-full bg-[#E63946] transition-all duration-500" style={{ width: `${progress}%` }} data-testid="progress-bar" />
              </div>
            </div>

            {/* Category badge */}
            {(() => {
              const cat = CATEGORIES.find(c => c.id === q.cat);
              return (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 text-[10px] uppercase tracking-wider font-heading border mb-4" style={{ color: cat?.color, borderColor: `${cat?.color}30` }}>
                  {cat && <cat.icon className="w-3 h-3" />} {cat?.label}
                </span>
              );
            })()}

            {/* Question */}
            <h2 className="text-xl md:text-2xl text-white font-heading mb-6" data-testid="current-question">{q.q}</h2>

            {/* Options */}
            <div className="space-y-3 mb-6" data-testid="options-list">
              {q.options.map(opt => {
                const isSelected = isMulti
                  ? (multiAnswers[q.id] || []).some(x => x.label === opt.label)
                  : answers[q.id] === opt.score;
                return (
                  <button key={opt.label}
                    onClick={() => isMulti ? handleMultiToggle(q.id, opt.label, opt.score) : handleSelect(q.id, opt.score)}
                    className={`w-full text-left px-5 py-4 border transition-all flex items-center justify-between group ${
                      isSelected ? 'border-[#E63946]/60 bg-[#E63946]/5 text-white' : 'border-zinc-800 bg-zinc-900/80 text-zinc-300 hover:border-zinc-700 hover:text-white'
                    }`}
                    data-testid={`option-${opt.label.slice(0, 20).toLowerCase().replace(/\s+/g, '-')}`}>
                    <span className="text-sm">{opt.label}</span>
                    {isSelected && <Check className="w-4 h-4 text-[#E63946] flex-shrink-0" />}
                  </button>
                );
              })}
            </div>

            {isMulti && (
              <p className="text-zinc-600 text-xs mb-3">Select all that apply, then click Next.</p>
            )}

            {/* Navigation */}
            <div className="flex gap-3">
              {currentQ > 0 && (
                <button onClick={() => setCurrentQ(prev => prev - 1)}
                  className="px-5 py-2.5 border border-zinc-700 text-zinc-400 font-heading text-sm uppercase tracking-wider hover:text-white transition-colors"
                  data-testid="prev-btn">
                  <ArrowLeft className="w-4 h-4 inline mr-1" /> Back
                </button>
              )}
              {isMulti && (
                <button onClick={handleMultiNext}
                  className="flex-1 py-2.5 bg-[#E63946] text-white font-heading text-sm uppercase tracking-wider hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                  data-testid="multi-next-btn">
                  Next <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </section>
      </main>
    );
  }

  // Results view
  return (
    <main className="pt-20 min-h-screen" data-testid="biohacking-score-page">
      <section className="container-ninja section-spacing">
        <div className="max-w-3xl mx-auto">
          {/* Score Hero */}
          <div className="text-center mb-10" data-testid="results-section">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#E63946] text-white text-xs font-heading tracking-widest uppercase mb-6">
              <Shield className="w-3.5 h-3.5" /> Your Biohacking Score
            </span>
            <div className="mb-4">
              <span className="text-7xl md:text-8xl font-heading" style={{ color: tier.color }} data-testid="overall-score">{overallScore}</span>
              <span className="text-zinc-600 text-3xl font-heading">/100</span>
            </div>
            <span className="inline-block px-4 py-1.5 text-sm font-heading uppercase tracking-wider border mb-3" style={{ color: tier.color, borderColor: `${tier.color}40`, backgroundColor: `${tier.color}10` }} data-testid="tier-label">{tier.label}</span>
            <p className="text-zinc-400 text-sm max-w-md mx-auto">{tier.desc}</p>
          </div>

          {/* Radar Chart — gated behind email */}
          <EmailResultsGate
            toolId="biohacking-score"
            toolName="Biohacking Score"
            resultPayload={{
              overall_score: overallScore,
              tier: tier.label,
              categories: catScores,
              weakest: weakest.map(c => c.label),
            }}
            ctaLabel="Unlock my full breakdown"
          >
            <div className="bg-zinc-900/80 border border-zinc-800 p-6 mb-6" data-testid="radar-section">
              <h3 className="text-white font-heading text-sm uppercase tracking-wider mb-4 text-center">Category Breakdown</h3>
              <RadarChart scores={catScores} />
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-6">
                {CATEGORIES.map(c => (
                  <div key={c.id} className="flex items-center gap-3 bg-zinc-800/50 border border-zinc-700 p-3" data-testid={`cat-score-${c.id}`}>
                    <c.icon className="w-4 h-4 flex-shrink-0" style={{ color: c.color }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-xs font-heading">{c.label}</p>
                      <div className="h-1.5 bg-zinc-700 mt-1 overflow-hidden">
                        <div className="h-full transition-all duration-700" style={{ width: `${(catScores[c.id] / 10) * 100}%`, backgroundColor: c.color }} />
                      </div>
                    </div>
                    <span className="text-sm font-heading" style={{ color: c.color }}>{catScores[c.id]}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Weakest areas */}
            {weakest.length > 0 && (
              <div className="bg-zinc-900/80 border border-[#f59e0b]/20 p-5 mb-6" data-testid="weakest-section">
                <h3 className="text-white font-heading text-sm uppercase tracking-wider mb-3 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-[#f59e0b]" /> Biggest Opportunities
                </h3>
                <div className="space-y-2">
                  {weakest.map(c => (
                    <div key={c.id} className="flex items-center gap-3">
                      <c.icon className="w-4 h-4" style={{ color: c.color }} />
                      <span className="text-white text-sm font-heading flex-1">{c.label}</span>
                      <span className="text-sm font-heading" style={{ color: c.color }}>{catScores[c.id]}/10</span>
                      <span className="text-zinc-500 text-xs">— most room to grow</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recommendations */}
            {recs.length > 0 && (
              <div className="mb-6" data-testid="recommendations-section">
                <h3 className="text-white font-heading text-sm uppercase tracking-wider mb-4 flex items-center gap-2">
                  <Star className="w-4 h-4 text-[#E63946]" /> Personalized Recommendations
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {recs.map((r, i) => (
                    <div key={r.text} className="bg-zinc-900/80 border border-zinc-800 p-4 flex items-start gap-3">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: r.catColor }} />
                      <div>
                        <p className="text-zinc-300 text-sm">{r.text}</p>
                        <Link to={r.link} className="text-xs font-heading uppercase tracking-wider mt-2 inline-flex items-center gap-1 hover:text-white transition-colors" style={{ color: r.catColor }}>
                          {r.linkLabel} <ArrowRight className="w-3 h-3" />
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </EmailResultsGate>

          {/* Action buttons */}
          <div className="flex flex-wrap gap-3 mb-6">
            <button onClick={handleSave} className="px-6 py-3 bg-[#00F0FF] text-black font-heading text-sm uppercase tracking-wider hover:opacity-90 transition-opacity flex items-center gap-2" data-testid="save-score-btn">
              {saved ? <><Check className="w-4 h-4" /> Saved</> : <><Save className="w-4 h-4" /> Save Score</>}
            </button>
            <button onClick={handleDownloadCard} className="px-6 py-3 bg-[#E63946] text-white font-heading text-sm uppercase tracking-wider hover:opacity-90 transition-opacity flex items-center gap-2" data-testid="download-card-btn">
              <Share2 className="w-4 h-4" /> Download Card
            </button>
            <button onClick={handleShare} className="px-6 py-3 border border-[#E63946]/40 text-[#E63946] font-heading text-sm uppercase tracking-wider hover:bg-[#E63946]/10 transition-colors flex items-center gap-2" data-testid="share-score-btn">
              {copied ? <><Check className="w-4 h-4" /> Copied</> : <><Share2 className="w-4 h-4" /> Share Score</>}
            </button>
            <button onClick={handleRetake} className="px-6 py-3 border border-zinc-700 text-zinc-400 font-heading text-sm uppercase tracking-wider hover:text-white transition-colors" data-testid="retake-btn">
              Retake Quiz
            </button>
          </div>

          {/* Cross-links */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Link to="/biohacking" className="group bg-zinc-900/80 border border-zinc-800 hover:border-[#E63946]/40 p-4 transition-all" data-testid="crosslink-biohacking">
              <h4 className="text-white text-sm font-heading group-hover:text-[#E63946] transition-colors">Biohacking Hub</h4>
              <p className="text-zinc-600 text-xs mt-1">Explore 27+ tools & devices</p>
            </Link>
            <Link to="/tools/recovery-stack" className="group bg-zinc-900/80 border border-zinc-800 hover:border-[#00F0FF]/40 p-4 transition-all" data-testid="crosslink-recovery">
              <h4 className="text-white text-sm font-heading group-hover:text-[#00F0FF] transition-colors">Recovery Stack Builder</h4>
              <p className="text-zinc-600 text-xs mt-1">Build your recovery protocol</p>
            </Link>
            <Link to="/tools/peptide-stack" className="group bg-zinc-900/80 border border-zinc-800 hover:border-[#E63946]/40 p-4 transition-all" data-testid="crosslink-peptides">
              <h4 className="text-white text-sm font-heading group-hover:text-[#E63946] transition-colors">Peptide Stack Builder</h4>
              <p className="text-zinc-600 text-xs mt-1">Multi-peptide protocol builder</p>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
